import { RpcError, FinishedUnaryCall } from '@protobuf-ts/runtime-rpc'
import { GrpcStatusCode } from 'shared/services/server-action/server-action.schema'
import { Info, Status } from 'shared/gen/pkg/proto/response'
import {
  SystemError,
  UserError,
  isSystemError,
  isUserError,
} from './application-error'

/** Standard gRPC status codes (google.rpc.Code) → string names used in protobuf-ts / GrpcStatusCode */
const GRPC_CODE_NUMBER_TO_NAME: Record<number, GrpcStatusCode> = {
  0: GrpcStatusCode.OK,
  1: GrpcStatusCode.CANCELLED,
  2: GrpcStatusCode.UNKNOWN,
  3: GrpcStatusCode.INVALID_ARGUMENT,
  4: GrpcStatusCode.DEADLINE_EXCEEDED,
  5: GrpcStatusCode.NOT_FOUND,
  6: GrpcStatusCode.ALREADY_EXISTS,
  7: GrpcStatusCode.PERMISSION_DENIED,
  8: GrpcStatusCode.RESOURCE_EXHAUSTED,
  9: GrpcStatusCode.FAILED_PRECONDITION,
  10: GrpcStatusCode.ABORTED,
  11: GrpcStatusCode.OUT_OF_RANGE,
  12: GrpcStatusCode.UNIMPLEMENTED,
  13: GrpcStatusCode.INTERNAL,
  14: GrpcStatusCode.UNAVAILABLE,
  15: GrpcStatusCode.DATA_LOSS,
  16: GrpcStatusCode.UNAUTHENTICATED,
}

const KNOWN_GRPC_STATUS_STRINGS = new Set<string>(Object.values(GrpcStatusCode))

/**
 * Maps numeric gRPC codes and unknown shapes to {@link GrpcStatusCode} strings.
 */
export function normalizeGrpcStatusCode(code: unknown): GrpcStatusCode {
  if (typeof code === 'number' && Number.isInteger(code)) {
    const mapped = GRPC_CODE_NUMBER_TO_NAME[code]
    return mapped ?? GrpcStatusCode.UNKNOWN
  }
  if (typeof code === 'string' && KNOWN_GRPC_STATUS_STRINGS.has(code)) {
    return code as GrpcStatusCode
  }
  return GrpcStatusCode.UNKNOWN
}

/**
 * Prefer `detail` (typical in @protobuf-ts); some transports use `message` instead.
 */
export function extractGrpcStatusMessage(status: unknown): string {
  if (!status || typeof status !== 'object') return ''
  const s = status as { detail?: unknown; message?: unknown }
  const detail = typeof s.detail === 'string' ? s.detail : ''
  const message = typeof s.message === 'string' ? s.message : ''
  return detail || message || ''
}

const rpcStatusErrorMap = new Map<
  GrpcStatusCode,
  typeof SystemError | typeof UserError
>([
  // generally caused by errors on the server side, obfuscate the error message shown to the user
  [GrpcStatusCode.ABORTED, SystemError],
  [GrpcStatusCode.DATA_LOSS, SystemError], // missing trailing headers
  [GrpcStatusCode.CANCELLED, SystemError],
  [GrpcStatusCode.DEADLINE_EXCEEDED, SystemError],
  [GrpcStatusCode.INTERNAL, SystemError],
  [GrpcStatusCode.OUT_OF_RANGE, SystemError],
  [GrpcStatusCode.UNAVAILABLE, SystemError],
  [GrpcStatusCode.UNIMPLEMENTED, SystemError],
  [GrpcStatusCode.UNKNOWN, SystemError],
  [GrpcStatusCode.RESOURCE_EXHAUSTED, SystemError],
  // invalid argument is considered a system error, because we should be more mindful of the user's input and pre-sanitize. If we're not, then we need to handle it and get in to fix it.
  [GrpcStatusCode.INVALID_ARGUMENT, SystemError],

  // generally caused by user actions
  // our system will expose the message from K2 to the user when we consider the grpc status a UserError
  [GrpcStatusCode.ALREADY_EXISTS, UserError],
  [GrpcStatusCode.FAILED_PRECONDITION, UserError],
  [GrpcStatusCode.NOT_FOUND, UserError],
  [GrpcStatusCode.PERMISSION_DENIED, UserError],
  [GrpcStatusCode.UNAUTHENTICATED, UserError],
])

/**
 * True when {@link rpcStatusErrorMap} classifies this code as a {@link UserError}.
 */
export function isUserFacingGrpcStatusCode(code: unknown): boolean {
  const normalized = normalizeGrpcStatusCode(code)
  return rpcStatusErrorMap.get(normalized) === UserError
}

export type K2UnaryCallResults<
  Input extends object,
  Output extends object,
> = Pick<FinishedUnaryCall<Input, Output>, 'status' | 'response' | 'method'> & {
  response: { info?: Info }
}

function isOK<Input extends object, Output extends { info?: Info }>(
  results: K2UnaryCallResults<Input, Output>,
): boolean {
  const rpcOk = results.status.code === 'OK'
  const k2Ok = results.response?.info?.status === Status.SUCCESS
  return rpcOk && k2Ok
}

export function makeUnaryResultsError<
  Input extends object,
  Output extends object,
>(results: K2UnaryCallResults<Input, Output>): SystemError | UserError {
  const statusCode = normalizeGrpcStatusCode(results.status.code)
  let ErrorClass: typeof SystemError | typeof UserError = SystemError
  if (rpcStatusErrorMap.has(statusCode)) {
    ErrorClass = rpcStatusErrorMap.get(statusCode) as
      | typeof SystemError
      | typeof UserError
  }

  // these should all be considered user errors as they've been explicitly handled by the server
  if (
    results.status.code === 'OK' &&
    results.response?.info?.status !== Status.SUCCESS
  ) {
    ErrorClass = UserError
  }

  const message =
    results.response?.info?.message ||
    extractGrpcStatusMessage(results.status) ||
    'Unknown Error'

  const hasMessage =
    !!results.response?.info?.message && results.response?.info?.message !== ''

  const handledRpcCode = [
    GrpcStatusCode.UNAUTHENTICATED,
    GrpcStatusCode.PERMISSION_DENIED,
  ].includes(statusCode)

  const handledUserErrorMessage =
    ErrorClass === UserError && message !== 'Unknown Error' && message !== ''

  return new ErrorClass(message, {
    grpcStatusCode: statusCode,
    originalError: results.status.detail,
    operationName: `${results.method?.service?.typeName || 'UnknownService'}.${results.method?.name || 'UnknownMethod'}`,
    handled: hasMessage || handledRpcCode || handledUserErrorMessage,
  })
}

export function makeErrorFromRpcError(
  error: RpcError | Error | unknown,
): SystemError | UserError {
  if (isUserError(error) || isSystemError(error)) return error

  if (!(error instanceof Error))
    return new SystemError(String(error), {
      originalError: error,
      handled: false,
    })

  const statusCode =
    error instanceof RpcError
      ? normalizeGrpcStatusCode(error.code)
      : GrpcStatusCode.UNKNOWN

  let ErrorClass: typeof SystemError | typeof UserError = SystemError
  if (rpcStatusErrorMap.has(statusCode)) {
    ErrorClass = rpcStatusErrorMap.get(statusCode) as
      | typeof SystemError
      | typeof UserError
  }

  const message = error.message || 'Unknown Error'
  const serviceName =
    error instanceof RpcError ? error.serviceName : 'UnknownService'
  const methodName =
    error instanceof RpcError ? error.methodName : 'UnknownMethod'

  const handledUserErrorMessage =
    ErrorClass === UserError && message !== 'Unknown Error' && message !== ''

  return new ErrorClass(message, {
    grpcStatusCode: statusCode,
    originalError: error,
    operationName: `${serviceName}.${methodName}`,
    handled: handledUserErrorMessage,
  })
}

export function handleRpcResponse<Input extends object, Output extends object>(
  results: K2UnaryCallResults<Input, Output>,
): K2UnaryCallResults<Input, Output> {
  if (isOK(results)) return results
  if (results.response instanceof RpcError)
    throw makeErrorFromRpcError(results.response)

  throw makeUnaryResultsError(results)
}

export function handleRpcError(error: RpcError): void {
  throw makeErrorFromRpcError(error)
}
